<script setup lang="ts">
  /**
   * Detail list — 03-components/13a-detail-card · OSCloud DS 1.1.170
   *
   * 13a's one-column shape: label at the leading edge, value at the trailing edge, a hairline
   * between rows. The rail's read-only register — a ticket's status and department, a service's
   * plan and renewal date.
   *
   * Not `BfDetailCard`, which is 13a's other shape: a two-column grid of stacked label-over-value
   * cells for a wide page section. This one is a narrow column read top to bottom. Read-only by
   * contract — a list whose rows are editable is a form, not this.
   */
  import type { BfDetailListRow } from './BfDetailListTypes'

  withDefaults(
    defineProps<{
      rows?: BfDetailListRow[]
      /** Labels read as primary text — for a list whose labels are the record, not its captions. */
      labelled?: boolean
      /**
       * Each row's icon sits in a 36px tinted frame instead of bare at 16px — for a quota or
       * resource list where the icon names the row's kind. Bare on every row or framed on every
       * row, so it is the list's choice, not the row's.
       */
      framed?: boolean
    }>(),
    { rows: () => [], labelled: false, framed: false },
  )
</script>

<template>
  <ul class="detail-list" :class="{ 'detail-list--labelled': labelled }">
    <li v-for="row in rows" :key="row.label" class="detail-list__row">
      <span v-if="row.icon && framed" class="icon-frame icon-frame--medium icon-frame--tinted" aria-hidden="true">
        <component :is="row.icon" :size="18" />
      </span>
      <component :is="row.icon" v-else-if="row.icon" class="detail-list__icon" :size="16" />
      <span v-if="row.sub" class="detail-list__text">
        <span class="detail-list__label">{{ row.label }}</span>
        <span class="detail-list__sub">{{ row.sub }}</span>
      </span>
      <span v-else class="detail-list__label">{{ row.label }}</span>
      <span class="detail-list__value" :class="row.tone ? `detail-list__value--${row.tone}` : null">
        <slot v-if="row.key" :name="`value-${row.key}`" :row="row">{{ row.value }}</slot>
        <template v-else>{{ row.value }}</template>
        <span v-if="row.secondary" class="detail-list__value-secondary">{{ row.secondary }}</span>
      </span>
    </li>
  </ul>
</template>

<style scoped lang="scss">
  .detail-list {
    display: flex;
    flex-direction: column;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .detail-list__row {
    display: flex;
    align-items: center;
    gap: var(--bf-sp-2);
    padding: 9px 0;
    border-bottom: 1px solid var(--bf-grey-light);
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-xs_5);
  }

  .detail-list__row:last-child {
    border-bottom: 0;
  }

  .detail-list__icon {
    flex: 0 0 auto;
    color: var(--bf-orange-dark);
  }

  /* A framed icon is the row's marker, so it keeps a little more air before the label. */
  .detail-list__row > .icon-frame {
    margin-right: calc(var(--bf-sp-2) - var(--bf-sp-1));
  }

  .detail-list__label {
    flex: 1 1 auto;
    min-width: 0;
    color: var(--bf-fg-2);
  }

  /* With a sub-line, label and sub stack in __text, which takes the free space instead of
     __label. The row still centres on the value, so the value never moves. */
  .detail-list__text {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .detail-list__text > .detail-list__label {
    flex: none;
  }

  .detail-list__sub {
    font-size: var(--bf-fs-xs);
    color: var(--bf-fg-2);
  }

  .detail-list--labelled .detail-list__label {
    font-weight: var(--bf-fw-medium);
    color: var(--bf-fg-1);
  }

  /* The value is the row's answer, so it takes the display face and the trailing edge. Tabular
     figures keep a column of numbers aligned down the list. */
  .detail-list__value {
    flex: 0 0 auto;
    font-family: var(--bf-font-display);
    font-weight: var(--bf-fw-semibold);
    font-variant-numeric: tabular-nums;
    text-align: right;
    color: var(--bf-fg-1);
  }

  .detail-list__value--success {
    color: var(--bf-success);
  }

  .detail-list__value--warning {
    color: var(--bf-warning-soft-text);
  }

  .detail-list__value--error {
    color: var(--bf-error-accent);
  }

  .detail-list__value--information {
    color: var(--bf-info-accent);
  }

  .detail-list__value--muted {
    color: var(--bf-fg-2);
  }

  /* An inline qualifier after the value — body face and regular weight, so it reads as an aside
     to the value rather than a second value. */
  .detail-list__value :deep(.detail-list__value-secondary),
  .detail-list__value-secondary {
    font-family: var(--bf-font-body);
    font-weight: var(--bf-fw-regular);
    color: var(--bf-fg-2);
  }
</style>
